// React Component
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { BarChart2, AlertCircle, Clock, LineChart } from 'lucide-react'

// App Component
import { UnifiedCard, NewProcessOrGroupV2 } from '../../components'
import { AppLayout, ProcessLayoutV2 } from '../../layouts'
import InterviewContextTopBar from '../interview/InterviewContextTopBar'
import CompanyAnalysisSidebar from '../../layouts/CompanyAnalysisSidebar'
import { Tutorial, FloatingTutorialButton } from '../../components'

// App Guidance components
import TutorialDataAttributes from '../../components/Tutorial/TutorialDataAttributes'

// Stores
import { useAppDispatch, useAppSelector, company } from '../../stores'

// Hooks personnalisés
import { useCompanyAnalysis } from './hooks/useCompanyAnalysis'
import { useFeedback } from '../../swimlane/components/AnalysisPanel/hooks/useFeedback'
import { useScrollToBottom } from '../../swimlane/components/AnalysisPanel/hooks/useScrollToBottom'
import { useFileUpload } from "../../swimlane/components/AnalysisPanel/hooks/useFileUpload"
import { useInsightsCollectionLinks } from './hooks/useInsightsCollectionLinks'
import { useTutorial } from '../../hooks/useTutorial'
import { analysisTutorialSteps } from '../../config/tutorialSteps'
import { useSearch } from '../../utils'
import { FamlaServices } from '../../services'
import { CompanyAnalysisService } from '../../services/api/companyAnalysisService'
import { getCollectAnalysisContext } from '../../services/api/collectDataService'
import { Routes } from '../../routes'
import { Images } from '../../constants'

// Composants internes
import { PromptInput } from '../../swimlane/components/AnalysisPanel/components/PromptInput'
import { ReportList } from '../../swimlane/components/AnalysisPanel/components/ReportList'
import { PromptSuggestions } from '../../swimlane/components/AnalysisPanel/components/PromptSuggestions'
import AnalysisFileModal from '../../swimlane/components/AnalysisPanel/components/AnalysisFileModal'
import { SaveAnalysisModal } from './components/SaveAnalysisModal'
import { NewAnalysisConfirmModal } from './components/NewAnalysisConfirmModal'
import {
    InsufficientCreditsModal,
} from '../modal'
import ChangeLanguageModal from '../modal/changeLanguage'

// Types
import { iMap, iProcess } from '../../components/type'
import { ScrollingNotification } from '../../components/notifications/ScrollingNotification'
import { useInterviewRailMessages } from '../../hooks/useInterviewRailNotifications'
import {
    clearCollectInsightsPreload,
    readCollectInsightsPreload,
    type CollectInsightsPreload,
} from '../../utils/collectInsightsPreload'
import {
    mergeMapsById,
    resolveMapIdsForCollection,
    resolveMapIdsForProcess,
    type SelectedCollectCollection,
} from '../../utils/analysisContextLinks'

import '../../styles/interview-redesign.css'
import './company-analysis-page.css'

const CompanyAnalysis = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const location = useLocation()
    const { analysisId: urlAnalysisId } = useParams<{ analysisId?: string }>()
    const { searchQuery, setSearchQuery, searchResults, setFilter, filter } = useSearch("/process/search/my/process")

    // == Sélecteurs ==
    const users = useAppSelector((state) => state.auth)
    const companyState = useAppSelector((state) => state.company.company)
    const role = useAppSelector((state) => state.role.role)
    const companyId = users.session.company_id

    React.useEffect(() => {
        const loadCompanyById = async () => {
            if (!companyId || !users.access_token) return
            try {
                const data = await FamlaServices("api_host", `/company/by/id/${companyId}`, "GET", "", users.access_token)
                if (data.status === 200) {
                    const companie = data.body.data
                    const newData = await FamlaServices(
                        "api_host",
                        `/request/total_tokens/company/?company_id=${companyId}`,
                        "GET",
                        "",
                        users.access_token
                    )
                    if (newData.status === 200) {
                        const tokenData = newData.body.data
                        const companyData = {
                            ...companie,
                            tokensRemaining: tokenData.left_token,
                            tokensUsed: tokenData.total_tokens,
                            totalTokens: tokenData.total_tokens,
                        }
                        dispatch(company({ company: companyData }))
                    }
                }
            } catch {
                // silencieux
            }
        }
        loadCompanyById()
    }, [companyId, users.access_token, dispatch])

    const deleProcess = async (process_id: string, _name: string): Promise<boolean> => {
        try {
            const response = await FamlaServices("api_host", `/process/${process_id}`, "DELETE", "", users.access_token)
            return response.status === 200
        } catch {
            return false
        }
    }

    // Déterminer si l'utilisateur actuel est le propriétaire de la compagnie ou un admin
    const isCompanyOwner = Boolean(
        companyState?.owner?._id &&
            users?.user?._id &&
            (companyState.owner._id === users.user._id ||
                companyState.owner._id?.toString() === users.user._id?.toString())
    )
    const isAdmin = role === "ADMIN" || role === "admin"
    const isOwner = isCompanyOwner || isAdmin
    // Récupérer l'email du propriétaire de la company pour les utilisateurs non-admin
    const [ownerEmail, setOwnerEmail] = React.useState<string | undefined>(undefined)

    // Récupérer l'email du propriétaire via l'API
    useEffect(() => {
        const loadOwnerEmail = async () => {
            if (isAdmin || !users.session?.company_id || !users.access_token) return

            try {
                const data = await FamlaServices("api_host", `/company/by/id/${users.session.company_id}`, "GET", "", users.access_token)

                if (data.status === 200 && data.body?.data?.owner?.email) {
                    setOwnerEmail(data.body.data.owner.email)
                }
            } catch {
                // Erreur silencieuse
            }
        }

        loadOwnerEmail()
    }, [isAdmin, users.session?.company_id, users.access_token])

    // == Refs ==
    const reportEndRef = useRef<HTMLDivElement | null>(null)
    const insufficientCreditsModalRef = useRef<HTMLSpanElement>(null)

    // == États locaux ==
    const [prompt, setPrompt] = useState('')
    const [isFileModalOpen, setIsFileModalOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const collectPreloadAppliedRef = useRef(false)
    const collectSynthesisOnlyRef = useRef(false)

    // == Hooks UI (déplacés avant useCompanyAnalysis pour accès à fileUploadProps) ==
    const { feedback, setFeedback, handleFeedbackClick, handleCommentSubmit } = useFeedback(users)
    const fileUploadProps = useFileUpload()

    const {
        displayInaccessibleLinks,
    } = useInsightsCollectionLinks({
        accessToken: users.access_token,
        selectedMaps: fileUploadProps.selectedMaps,
        selectedCollections: fileUploadProps.selectedCollections,
        onCollectionsChange: (collections) => fileUploadProps.handleCollectionSelect(collections),
    })

    const scopeInfoMessage = useMemo(() => {
        if (!displayInaccessibleLinks.length) return null
        const names = displayInaccessibleLinks.map((l) => l.name).join(', ')
        return t('companyAnalysis.insightsLinkedCollectionInaccessible', { names })
    }, [displayInaccessibleLinks, t])

    // Tutorial setup — mémoriser les steps pour éviter une boucle Joyride (nouvelle ref à chaque render)
    const tutorialSteps = useMemo(() => analysisTutorialSteps(t), [t])
    const {
        run: runTutorial,
        stepIndex,
        steps,
        startTutorial,
        handleJoyrideCallback,
        resetTutorialProgress,
    } = useTutorial(tutorialSteps, 'analysis', true)

    // Callback pour restaurer les cartographies depuis leurs IDs (endpoint summary léger)
    const handleRestoreFiles = async (
        mapIds: string[],
        knownMaps: iMap[] = [],
        replace = false,
    ) => {
        try {
            if (replace && !mapIds.length) {
                fileUploadProps.handleMapSelect(null);
                return knownMaps;
            }

            const companyMaps = knownMaps.length
                ? knownMaps
                : await CompanyAnalysisService.listMappingSummaries(
                    users.session.company_id,
                    users.access_token,
                    mapIds.length ? mapIds : undefined,
                )

            const mapsToRestore = mapIds.length
                ? companyMaps.filter((map: iMap) => mapIds.includes(map._id))
                : []

            if (mapsToRestore.length > 0) {
                fileUploadProps.handleMapSelect(
                    replace
                        ? mapsToRestore
                        : mergeMapsById(fileUploadProps.selectedMaps, mapsToRestore),
                )
            } else if (replace) {
                fileUploadProps.handleMapSelect(null);
            }
            return companyMaps
        } catch (error) {
            console.error('Erreur lors de la restauration des cartographies:', error)
        }
        return knownMaps
    }

    const applyCollectPreload = async (preload: CollectInsightsPreload) => {
        if (!users.access_token) return;

        const ctxRes = await getCollectAnalysisContext(preload.collectionId, users.access_token);
        const hasResponses = ctxRes.status === 200 && ctxRes.body?.data?.has_responses === true;
        if (!hasResponses) return;

        const collectionEntry: SelectedCollectCollection = {
            id: preload.collectionId,
            name: preload.collectionName,
            map_ids: [...(preload.mapIds || [])],
            linked_process_id: preload.linkedProcessId ?? null,
        }
        fileUploadProps.handleCollectionSelect([collectionEntry])

        let mapIds = preload.mapIds?.filter(Boolean) || []
        if (mapIds.length) {
            await handleRestoreFiles(mapIds, [], true)
            return
        }

        const linkedProcessId = collectionEntry.linked_process_id ?? preload.linkedProcessId ?? null
        if (!linkedProcessId) {
            fileUploadProps.handleMapSelect(null)
            return
        }

        const allMaps = await CompanyAnalysisService.listMappingSummaries(
            users.session.company_id,
            users.access_token,
        )
        mapIds = await resolveMapIdsForCollection(collectionEntry, allMaps)
        if (!mapIds.length) {
            mapIds = resolveMapIdsForProcess(allMaps, linkedProcessId)
        }
        if (mapIds.length) {
            await handleRestoreFiles(mapIds, allMaps, true)
        } else {
            fileUploadProps.handleMapSelect(null)
        }
    }

    // Callback pour restaurer les fichiers uploadés depuis le backend
    const handleRestoreUploadedFiles = async (files: any[]) => {
        try {
            for (const fileData of files) {
                if (fileData.url) {
                    const response = await fetch(fileData.url)
                    const blob = await response.blob()
                    const fileName = fileData.url.split('/').pop() || `file_${fileData.id}`
                    const file = new File([blob], fileName, { type: blob.type })
                    fileUploadProps.handleFileSelect(file)
                }
            }
        } catch (error) {
            console.error('Erreur lors de la restauration des fichiers uploadés:', error)
        }
    }

    // Callback pour restaurer les collectes depuis une analyse sauvegardée ou le draft
    const handleRestoreCollections = (collections: SelectedCollectCollection[]) => {
        fileUploadProps.handleCollectionSelect(collections);
    }

    // == Hook principal ==
    const {
        reports,
        isLoading,
        analysisId,
        analysisState,
        showSaveModal,
        showNewAnalysisModal,
        confirmModalVariant,
        hasUnsavedChanges,
        savedAnalysisName,
        setSavedAnalysisName,
        lastSaved,
        insufficientCreditsInfo,
        handleSubmitPrompt,
        handleLoadAnalysis,
        handleNewAnalysis,
        handleSaveAnalysis,
        confirmNewAnalysis,
        cancelNewAnalysisModal,
        cancelSaveModal,
        resetAnalysis,
        setShowSaveModal,
        setShowNewAnalysisModal,
        setAnalysisState,
        setLastSaved,
        setInsufficientCreditsInfo,
    } = useCompanyAnalysis({
        accessToken: users.access_token,
        userId: users.user._id || "",
        companyId: users.session.company_id || "",
        onResetFiles: () => fileUploadProps.clearSelection(),
        onRestoreFiles: (mapIds, options) => handleRestoreFiles(mapIds, [], options?.replace ?? false),
        onRestoreUploadedFiles: handleRestoreUploadedFiles,
        onRestoreCollections: handleRestoreCollections,
        selectedMapIds: fileUploadProps.selectedMapIds,
        selectedCollections: fileUploadProps.selectedCollections,
        urlAnalysisId,
    })

    useScrollToBottom(reportEndRef, { reports, feedback })

    // == Chargement automatique de l'analyse depuis l'URL ==
    useEffect(() => {
        if (!urlAnalysisId) return
        if (urlAnalysisId === analysisId) return
        handleLoadAnalysis(urlAnalysisId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlAnalysisId, analysisId])

    // == Préchargement depuis Collect Data (Synthèse → Insights) ==
    useEffect(() => {
        if (collectPreloadAppliedRef.current || urlAnalysisId) return;
        const fromState = (location.state as { collectInsightsPreload?: CollectInsightsPreload } | null)
            ?.collectInsightsPreload;
        const preload = fromState ?? readCollectInsightsPreload();
        if (!preload?.collectionId) {
            if (preload) clearCollectInsightsPreload();
            return;
        }
        collectPreloadAppliedRef.current = true;
        collectSynthesisOnlyRef.current = preload.synthesisOnly !== false;
        void applyCollectPreload(preload).finally(() => clearCollectInsightsPreload());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, urlAnalysisId]);

    // == Ouvrir le modal de crédits insuffisants quand nécessaire ==
    useEffect(() => {
        if (insufficientCreditsInfo && insufficientCreditsModalRef.current) {
            setTimeout(() => {
                if (insufficientCreditsModalRef.current) {
                    insufficientCreditsModalRef.current.click()
                }
            }, 100)
        }
    }, [insufficientCreditsInfo])

    // == Effacer le message d'erreur quand un document est sélectionné ==
    useEffect(() => {
        const hasSelectedFiles = fileUploadProps.selectedFiles && fileUploadProps.selectedFiles.length > 0
        const hasSelectedMaps = fileUploadProps.selectedMaps && fileUploadProps.selectedMaps.length > 0
        const hasSelectedCollections =
            fileUploadProps.selectedCollections && fileUploadProps.selectedCollections.length > 0

        if (errorMessage && (hasSelectedFiles || hasSelectedMaps || hasSelectedCollections)) {
            setErrorMessage(null)
        }
    }, [fileUploadProps.selectedFiles, fileUploadProps.selectedMaps, fileUploadProps.selectedCollections, errorMessage])

    // == Fonctions ==
    const handleSubmit = async (promptText: string) => {
        setErrorMessage(null)

        if (!promptText.trim()) {
            setErrorMessage(t('companyAnalysis.errorEmptyPrompt') || 'Veuillez saisir une question ou une instruction')
            return
        }

        const hasSelectedFiles = fileUploadProps.selectedFiles && fileUploadProps.selectedFiles.length > 0
        const hasSelectedMaps = fileUploadProps.selectedMaps && fileUploadProps.selectedMaps.length > 0
        const hasSelectedCollections =
            fileUploadProps.selectedCollections && fileUploadProps.selectedCollections.length > 0

        if (!hasSelectedFiles && !hasSelectedMaps && !hasSelectedCollections) {
            setErrorMessage(
                t('companyAnalysis.errorNoDocuments') || "Veuillez sélectionner au moins un document avant de commencer l'analyse"
            )
            return
        }

        if (!hasSelectedMaps && !hasSelectedCollections) {
            setErrorMessage(
                t('companyAnalysis.errorNoMapOrCollection')
                || 'Veuillez sélectionner au moins une cartographie ou une collecte',
            )
            return
        }

        const linkedProcessId = fileUploadProps.selectedCollections?.[0]?.linked_process_id ?? null

        const success = await handleSubmitPrompt(
            promptText,
            fileUploadProps.selectedMaps,
            fileUploadProps.selectedFiles.length > 0 ? fileUploadProps.selectedFiles : null,
            fileUploadProps.selectedCollectionIds,
            {
                linkedProcessId,
                synthesisOnly: collectSynthesisOnlyRef.current,
            },
        )
        if (success) {
            setPrompt('')
            setErrorMessage(null)
            collectSynthesisOnlyRef.current = false
        }
    }

    const handleCopyText = (text: string) => {
        navigator.clipboard.writeText(text)
    }

    const handleSaveReport = async (reportId: string, name: string): Promise<boolean> => {
        const { CompanyAnalysisService } = await import('../../services/api/companyAnalysisService')

        if (analysisId) {
            await CompanyAnalysisService.saveAnalysis(analysisId, name, users.access_token, {
                map_ids: fileUploadProps.selectedMapIds,
                collection_ids: fileUploadProps.selectedCollectionIds,
            })
        } else {
            await CompanyAnalysisService.saveReportAnalysis(reportId, name, users.access_token)
        }

        setLastSaved(Date.now())
        resetAnalysis()

        return true
    }

    const handleOpenFileModal = () => {
        setIsFileModalOpen(true)
    }

    const handleCloseFileModal = () => {
        setIsFileModalOpen(false)
    }

    const handleFileModalConfirm = (options: {
        file?: File;
        files?: File[];
        maps?: iMap[];
        collections?: SelectedCollectCollection[];
    }) => {
        if (options.files && options.files.length > 0) {
            options.files.forEach((file) => {
                fileUploadProps.handleFileSelect(file)
            })
        } else if (options.file) {
            fileUploadProps.handleFileSelect(options.file)
        }

        if (options.maps) {
            fileUploadProps.handleMapSelect(options.maps)
        }
        if (options.collections?.length) {
            fileUploadProps.handleCollectionSelect(
                options.collections.filter((c) => c.id && c.name),
            )
        }
        setIsFileModalOpen(false)
    }

    // == Suggestions ==
    const promptSuggestions = [
        {
            icon: <BarChart2 size={14} strokeWidth={2} />,
            title: t('features.swimlane.summaryStatistics'),
            description: t('features.swimlane.analyzeAllAttributesData'),
            prompt: t('features.swimlane.prompt1'),
            tone: 'blue' as const,
        },
        {
            icon: <AlertCircle size={16} strokeWidth={2} />,
            title: t('features.swimlane.bottleneckIdentification'),
            description: t('features.swimlane.identifyStakeholders'),
            prompt: t('features.swimlane.prompt2'),
            tone: 'coral' as const,
        },
        {
            icon: <Clock size={16} strokeWidth={2} />,
            title: t('features.swimlane.leadTimeAnalysis'),
            description: t('features.swimlane.analyzeLead'),
            prompt: t('features.swimlane.prompt3'),
            tone: 'amber' as const,
        },
        {
            icon: <LineChart size={16} strokeWidth={2} />,
            title: t('features.swimlane.qualityAnalysis'),
            description: t('features.swimlane.examineDefect'),
            prompt: t('features.swimlane.prompt4'),
            tone: 'accent' as const,
        },
    ]

    const showSearch = (searchQuery ?? '') !== ''
    const hasReports = reports && reports.length > 0

    // Share modal
    const shareModalTriggerRef = useRef<HTMLSpanElement>(null)
    const [shareModalCopied, setShareModalCopied] = useState(false)
    const handleShareAnalysis = () => {
        shareModalTriggerRef.current?.click()
    }
    const copyAnalysisLink = () => {
        const link = analysisId
            ? `${window.location.origin}/company/analysis/${analysisId}`
            : window.location.href
        navigator.clipboard.writeText(link).catch(() => {
            const el = document.createElement('textarea')
            el.value = link
            document.body.appendChild(el)
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
        })
        setShareModalCopied(true)
        setTimeout(() => setShareModalCopied(false), 3000)
    }

    return (
        <>
            <button
                type="button"
                id="company-analysis-new-trigger"
                className="d-none"
                aria-hidden
                tabIndex={-1}
                onClick={() => handleNewAnalysis()}
            />
            <AppLayout>
                {/* Structure identique à ModernInterviewLayout */}
                <div className="interview-shell-container">
                    <div className="interview-shell">
                        <div className="shell">

                            {/* LEFT PANEL — même classe que ModernInterviewLayout */}
                            <CompanyAnalysisSidebar
                                onNewDocument={handleOpenFileModal}
                                onNewAnalysis={handleNewAnalysis}
                                onSelectAnalysis={handleLoadAnalysis}
                                onDeleteAnalysis={resetAnalysis}
                                selectedFile={fileUploadProps.selectedFile}
                                selectedFiles={fileUploadProps.selectedFiles}
                                selectedMaps={fileUploadProps.selectedMaps}
                                selectedCollections={fileUploadProps.selectedCollections}
                                onRemoveFile={fileUploadProps.removeFile}
                                onRemoveMap={fileUploadProps.removeMap}
                                onRemoveCollection={fileUploadProps.removeCollection}
                                inaccessibleLinks={displayInaccessibleLinks}
                                analysisState={analysisState}
                                lastSaved={lastSaved}
                                currentAnalysisId={analysisId}
                                onSearch={setSearchQuery}
                            />

                            {/* CENTRE — même structure que ModernInterviewLayout */}
                            <div className="centre ca-analysis-centre">
                                <div className="c-topbar">
                                    <InterviewContextTopBar
                                        processTitle={savedAnalysisName || t('features.swimlane.analysis') || 'Analyse'}
                                        ctaLabel={t('features.swimlane.newInsight')}
                                        ctaOnClick={handleNewAnalysis}
                                    />
                                </div>

                                {showSearch ? (
                                    <div className="ca-search-shell home-dashboard flex-grow-1 overflow-auto min-h-0 w-100">
                                        <div className="home-dashboard-inner">
                                            <ProcessLayoutV2
                                                title={`${t('words.searching')}...${searchQuery}`}
                                                arrowBack={false}
                                                style={{}}
                                                classBlockHeader="justify-content-center"
                                                classNameWrapper="justify-content-center"
                                            >
                                                <>
                                                    {searchResults?.slice(0, 8).map((proc: iProcess) => (
                                                        <UnifiedCard
                                                            processName={proc.title}
                                                            thisProcessHasABadge={false}
                                                            theBadgeHasAValue={false}
                                                            badgeValue={0}
                                                            thisProcessHasAOwner={true}
                                                            owner={users.user.fullname === proc.owner.fullname ? t('words.me') : proc.owner.fullname}
                                                            key={proc._id}
                                                            route={Routes.PROCESS_INTERVIEW}
                                                            deleteProcess={async () => await deleProcess(proc._id, proc.title)}
                                                            description={proc.description}
                                                            processId={proc._id}
                                                            folder={false}
                                                            setProject={() => {}}
                                                            setProcess={() => {}}
                                                            elementType="process"
                                                            bgImage={Images.folder}
                                                            t={t}
                                                            isSample={proc.is_sample}
                                                        />
                                                    ))}
                                                    {searchResults.length === 0 && (
                                                        <NewProcessOrGroupV2
                                                            newElement={t('views.processView.processNotFound')}
                                                            create="process"
                                                            noResult={true}
                                                        />
                                                    )}
                                                </>
                                            </ProcessLayoutV2>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ca-analysis-workspace">
                                        <div className="ca-main-shell">
                                            <div
                                                className={hasReports ? 'ca-reports-wrap flex-grow-1' : 'ca-prompt-scroll'}
                                                style={hasReports ? { overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0 } : undefined}
                                            >
                                                {hasReports ? (
                                                    <div data-tutorial="analysis-reports">
                                                        <div data-tutorial="analysis-charts">
                                                            <ReportList
                                                                reports={reports}
                                                                isLoading={isLoading}
                                                                feedback={feedback}
                                                                onFeedbackClick={handleFeedbackClick}
                                                                onCommentSubmit={handleCommentSubmit}
                                                                onCopyText={handleCopyText}
                                                                onSaveReport={handleSaveReport}
                                                                setFeedback={setFeedback}
                                                                onResponse={handleSubmit}
                                                                setReport={() => {}}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="mx-auto ca-analysis-hero-inner w-100">
                                                        <div className="ca-hero-row">
                                                            <div className="ca-hero-title-wrap">
                                                                <h1 className="ca-hero-title">
                                                                    {savedAnalysisName || t('features.swimlane.reportGenerate') || "Quelle analyse voulez-vous effectuer aujourd'hui ?"}
                                                                </h1>
                                                                <p className="ca-hero-sub mb-4">{t('companyAnalysis.heroSubtitle')}</p>
                                                            </div>
                                                            {hasUnsavedChanges && (
                                                                <span className="ca-unsaved-badge">
                                                                    <i className="fa-solid fa-circle-exclamation me-1"></i>
                                                                    {t('features.swimlane.unsaved') || 'Non sauvegardé'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {errorMessage && (
                                                            <div className="ca-error-banner">
                                                                <div className="d-flex align-items-center">
                                                                    <i className="fa-solid fa-triangle-exclamation me-2" style={{ color: 'var(--coral, #e8644a)' }}></i>
                                                                    <p className="mb-0">{errorMessage}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {scopeInfoMessage && (
                                                            <div
                                                                className="rounded-3 px-3 py-2 mb-3 small"
                                                                style={{ background: '#fff8e6', border: '1px solid #f0d78c', color: '#7a5c00' }}
                                                            >
                                                                <i className="fa-solid fa-circle-info me-2" />
                                                                {scopeInfoMessage}
                                                            </div>
                                                        )}
                                                        <div data-tutorial="analysis-suggestions">
                                                            <PromptSuggestions
                                                                suggestions={promptSuggestions}
                                                                onSuggestionClick={handleSubmit}
                                                                variant="modern"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div ref={reportEndRef} />
                                            </div>
                                            <div data-tutorial="analysis-input">
                                                <PromptInput
                                                    prompt={prompt}
                                                    setPrompt={setPrompt}
                                                    isLoading={isLoading}
                                                    onSubmit={handleSubmit}
                                                    swimlanes={[]}
                                                    fileUploadProps={fileUploadProps}
                                                    isDisableInput={false}
                                                    variant="modern"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* RIGHT PANEL — même position que ModernInterviewLayout */}
                            {!showSearch && (
                                <AnalysisRightPanel
                                    hasDocuments={
                                        (fileUploadProps.selectedFiles?.length ?? 0) > 0
                                        || (fileUploadProps.selectedMaps?.length ?? 0) > 0
                                        || (fileUploadProps.selectedCollections?.length ?? 0) > 0
                                    }
                                    hasReports={hasReports}
                                    analysisState={analysisState}
                                    onNewAnalysis={handleNewAnalysis}
                                    onAddDocument={handleOpenFileModal}
                                    onSave={() => setShowSaveModal(true)}
                                    onShare={handleShareAnalysis}
                                />
                            )}

                        </div>
                    </div>
                </div>
            </AppLayout>

            {/* Modals */}
            <AnalysisFileModal
                isOpen={isFileModalOpen}
                onClose={handleCloseFileModal}
                onConfirm={handleFileModalConfirm}
                selectedFile={fileUploadProps.selectedFile}
                selectedMapIds={fileUploadProps.selectedMapIds}
                selectedMaps={fileUploadProps.selectedMaps}
                selectedCollectionIds={fileUploadProps.selectedCollectionIds}
                selectedCollections={fileUploadProps.selectedCollections}
                swimlanes={[]}
                fileError={fileUploadProps.fileError}
                handleFileSelect={fileUploadProps.handleFileSelect}
                handleMapSelect={fileUploadProps.handleMapSelect}
                handleCollectionSelect={fileUploadProps.handleCollectionSelect}
                clearSelection={fileUploadProps.clearSelection}
            />

            <SaveAnalysisModal
                isOpen={showSaveModal}
                onSave={handleSaveAnalysis}
                onClose={cancelSaveModal}
                defaultName={savedAnalysisName}
            />

            <NewAnalysisConfirmModal
                isOpen={showNewAnalysisModal}
                onConfirm={confirmNewAnalysis}
                onCancel={cancelNewAnalysisModal}
                variant={confirmModalVariant}
            />

            {/* Trigger caché pour le modal share */}
            <span ref={shareModalTriggerRef} data-bs-target="#shareModalAnalysis" data-bs-toggle="modal" className="d-none" />

            {/* Modal Share Analysis */}
            <div className="modal fade" id="shareModalAnalysis" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content py-4 px-3 border-0 bg-dark text-white">
                        <div className="modal-header d-flex justify-content-between p-0 pb-3 mb-3">
                            <h5 className="mb-0 fs-5">{t('navigation.shareLink')}</h5>
                            <button className="btn-close bg-white" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div>
                            <div className="d-flex align-items-center border rounded-3 px-2 py-1 w-100">
                                <span className="me-2" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('words.link')}</span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm p-0 rounded-3 fw-light border-0 bg-dark text-white"
                                    value={analysisId ? `${window.location.origin}/company/analysis/${analysisId}` : window.location.href}
                                    readOnly
                                />
                                <button
                                    className="p-2 border-0 rounded-2 px-3 ms-1 bg-secondary text-white"
                                    onClick={copyAnalysisLink}
                                >
                                    {shareModalCopied ? t('words.copied') : t('words.copy')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <span ref={insufficientCreditsModalRef} data-bs-target={`#insufficientCreditsModalAnalysis`} data-bs-toggle='modal'></span>
            {insufficientCreditsInfo && (
                <InsufficientCreditsModal
                    id="insufficientCreditsModalAnalysis"
                    creditsRequired={insufficientCreditsInfo.creditsRequired}
                    creditsAvailable={insufficientCreditsInfo.creditsAvailable}
                    deficit={insufficientCreditsInfo.deficit}
                    plan={insufficientCreditsInfo.plan}
                    onClose={() => setInsufficientCreditsInfo(null)}
                    isOwner={isOwner}
                    adminEmail={ownerEmail}
                />
            )}

            <ChangeLanguageModal />

            <Tutorial
                run={runTutorial}
                steps={steps}
                stepIndex={stepIndex}
                callback={handleJoyrideCallback}
                continuous={true}
                showProgress={true}
                showSkipButton={true}
            />

            <FloatingTutorialButton onClick={startTutorial} />

            <TutorialDataAttributes />
        </>
    )
}

interface AnalysisRightPanelProps {
    hasDocuments: boolean
    hasReports: boolean
    analysisState: string
    onNewAnalysis: () => void
    onAddDocument: () => void
    onSave: () => void
    onShare?: () => void
}

const AnalysisRightPanel: React.FC<AnalysisRightPanelProps> = ({
    onNewAnalysis,
    onShare,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const railMessages = useInterviewRailMessages()

    const signupUrl = useMemo(() => `${window.location.origin}${Routes.AUTH_SIGNIN}`, [])

    const recommendFamla = useCallback(async () => {
        const title = t('form.collectWorkspace.contributorRecommendShareTitle', '{{appName}}')
        const text = t('form.collectWorkspace.contributorRecommendShareText', {
            url: signupUrl,
            defaultValue: 'Try {{appName}} for qualitative research and interviews: {{url}}',
        })
        try {
            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({ title, text, url: signupUrl })
                return
            }
        } catch (e: unknown) {
            const err = e as { name?: string }
            if (err?.name === 'AbortError') return
        }
        const subject = encodeURIComponent(
            t('form.collectWorkspace.contributorRecommendEmailSubject', {
                defaultValue: 'I thought you might like {{appName}}',
            }),
        )
        const body = encodeURIComponent(
            t('form.collectWorkspace.contributorRecommendEmailBody', {
                url: signupUrl,
                defaultValue:
                    "Hi,\n\nI've been using {{appName}} for interviews and qualitative research — here's where to sign up:\n{{url}}\n",
            }),
        )
        window.location.href = `mailto:?subject=${subject}&body=${body}`
    }, [signupUrl, t])

    const steps = [
        { title: t('companyAnalysis.step1Title'), desc: t('companyAnalysis.step1Desc') },
        { title: t('companyAnalysis.step2Title'), desc: t('companyAnalysis.step2Desc') },
        { title: t('companyAnalysis.step3Title'), desc: t('companyAnalysis.step3Desc') },
        { title: t('companyAnalysis.step4Title'), desc: t('companyAnalysis.step4Desc') },
        { title: t('companyAnalysis.step5Title'), desc: t('companyAnalysis.step5Desc') },
    ]

    return (
        <div className="right-panel ca-analysis-right-panel">
            <div className="rp-scroll">
                {/* Bloc 1 : Before you start */}
                <div className="rp-sec">
                    <div className="rp-title">{t('companyAnalysis.beforeYouStart')}</div>
                    <ol className="ca-rp-steps-list">
                        {steps.map((step, idx) => (
                            <li key={idx} className="ca-rp-step-item">
                                <span className="ca-rp-step-title">{step.title}</span>
                                <span className="ca-rp-step-desc">{step.desc}</span>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Bloc 2 : Quick Actions — mêmes clés que l'Interview */}
                <div className="rp-sec rp-sec--quick">
                    <div className="rp-title">{t('words.quickActions')}</div>
                    <div
                        className="qa-row"
                        onClick={onNewAnalysis}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && onNewAnalysis()}
                    >
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                            <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                        </svg>
                        {t('features.swimlane.newInsight')}
                    </div>
                    <div
                        className="qa-row sec"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(Routes.PROCESSLIBRARY)}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(Routes.PROCESSLIBRARY)}
                    >
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                            <path d="M2 5H8M2 8H12M2 11H10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                        </svg>
                        {t('views.processInterview.openProcessLibrary') || t('words.library')}
                    </div>
                    <div
                        className="qa-row sec"
                        role="button"
                        tabIndex={0}
                        onClick={() => onShare?.()}
                        onKeyDown={(e) => e.key === 'Enter' && onShare?.()}
                    >
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                            <circle cx="4" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
                            <circle cx="12" cy="4" r="2" stroke="currentColor" strokeWidth="1.2" />
                            <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.2" />
                            <path d="M6 7L10 5M6 9L10 11" stroke="currentColor" strokeWidth="1.2" />
                        </svg>
                        {t('views.processInterview.share') || t('words.share')}
                    </div>
                    <button type="button" className="qa-row sec" onClick={() => void recommendFamla()}>
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {t('form.collectWorkspace.contributorQuickRecommendFriend', 'Recommend {{appName}} to a friend')}
                    </button>
                </div>
            </div>

            <div className="rp-announce-wrap">
                <ScrollingNotification
                    className="rp-scrolling-notification"
                    messages={railMessages}
                    onDismiss={(messageId) => {
                        console.log('Dismissed notification:', messageId)
                    }}
                    onClick={(message) => {
                        console.log('Clicked notification:', message)
                    }}
                />
            </div>
        </div>
    )
}

export default CompanyAnalysis
